import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { firstValueFrom } from 'rxjs';
import { GptCompletionMessage } from '../../../../common/general-types/open-ai.dto';
import { ChatGptService } from '../../../../services/chat-gpt/chat-gpt.service';
import { NotificationType } from '../../../../common/app-config/filter-models/misc';
import { WysiswygComponent } from '../../../../atoms/wysiswyg/wysiswyg.component';

@Component({
  selector: 'agent-step-documentation',
  standalone: true,
  imports: [CommonModule, FormsModule, WysiswygComponent],
  templateUrl: './documentation.component.html',
  styleUrl: './documentation.component.css'
})
export class DocumentationComponent implements OnInit {

  quickStartOptions: { id: number, colour: string, name: string, description: string, prompt: string }[] = [
    {
      id: 0,
      colour: 'bg-blue-500',
      name: 'Showcase',
      description: 'Highlights the product and builds trust.',
      prompt: 'Instruct the creator to showcase the product and build trust.'
    },
    {
      id: 1,
      colour: 'bg-green-500',
      name: 'Testimonial',
      description: 'A testimonial from a happy customer.',
      prompt: 'Instruct the creator to give a testimonial from a happy customer.'
    },
    {
      id: 2,
      colour: 'bg-yellow-500',
      name: 'How-To',
      description: 'Shows how to use the product.',
      prompt: 'Instruct the creator to show how to use the product'
    },
    {
      id: 3,
      colour: 'bg-red-500',
      name: 'Unboxing',
      description: 'Unboxes the product and shows what is inside.',
      prompt: 'Instruct the creator to unbox the product and show what is inside.'
    },
    {
      id: 4,
      colour: 'bg-primary',
      name: 'Review',
      description: 'Reviews the product and gives an honest opinion.',
      prompt: 'Instruct the creator to review the product and give an honest opinion.'
    },
    {
      id: 5,
      colour: 'bg-orange-500',
      name: 'Comparison',
      description: 'Shows how the product compares to other products.',
      prompt: 'Instruct the creator to show how the product compares to other products.'
    },
    {
      id: 6,
      colour: 'bg-pink-500',
      name: 'Basic',
      description: 'Create a simple starter brief',
      prompt: 'Create a simple starter brief considering the product and the campaign.'
    },
  ];

  systemMessage: GptCompletionMessage = {
    role: 'system',
    content: `You are a machine that only returns and replies with valid, iterable RFC8259 compliant JSON in your responses. Your text and punctuation should be compliant for white-space: pre-line.
     You are a social media expert helping a brand come up with a brief to instruct content creators.`,
  };

  /** Services */
  public createBriefService = inject(CreateBriefService);
  private currentUserService = inject(CurrentUserService);
  private chatGptService = inject(ChatGptService);
  /** Services - END */

  messages = signal<Array<{ text: string; displayedText: string; isTyping: boolean; isComplete: boolean; isUser?: boolean }>>([]);
  currentMessageIndex = signal<number>(0);
  showMessageInput = signal<boolean>(false);
  userInput: string = '';
  private secondMessage: string = '';
  hasUserSelectedOption = signal<boolean>(false);
  selectedQuickStartOption = signal<{ id: number, colour: string, name: string, description: string, prompt: string } | undefined>(undefined);

  queryRunning = signal<boolean>(false);
  startPrompt = signal<string | undefined>(undefined);

  reviewContentMode = signal<boolean>(false);

  openAiMessages: GptCompletionMessage[] = [this.systemMessage];

  ngOnInit(): void {
    this.configureFirstMessage();
  }

  async configureFirstMessage() {
    const user = await firstValueFrom(this.currentUserService.getShoutUser());
    const firstName = user?.firstName ?? '';
    const productName = this.createBriefService.selectedProduct()?.productName ?? '';
    const goal = this.createBriefService.selectedBriefGoal()?.description ?? '';

    const firstMessage =
      `Hey ${firstName} 👋,
      I can see that you'd like to create a brief for ${productName} to ${goal}.
      Help me out with a few bits of information so that I can build a brief for you.`;

    // Store second message for later
    this.secondMessage =
      `Would you like to select one of the suggested options below, or would you prefer to type in your own answer?`;

    // Initialize messages array with only the first message
    this.messages.set([
      { text: firstMessage, displayedText: '', isTyping: false, isComplete: false, isUser: false }
    ]);

    // Start typing the first message
    this.startTypewriter(0);
  }

  private startTypewriter(messageIndex: number): void {
    const messages = this.messages();
    if (messageIndex >= messages.length) {
      return;
    }

    const message = messages[messageIndex];
    if (!message || message.text.length === 0) {
      return;
    }

    // Mark message as typing
    const updatedMessages = [...messages];
    updatedMessages[messageIndex] = { ...message, isTyping: true };
    this.messages.set(updatedMessages);

    let currentIndex = 0;
    const typingSpeed = 30; // milliseconds per character

    const typeNextChar = () => {
      if (currentIndex < message.text.length) {
        const updatedMessages = [...this.messages()];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          displayedText: message.text.substring(0, currentIndex + 1)
        };
        this.messages.set(updatedMessages);
        currentIndex++;
        setTimeout(typeNextChar, typingSpeed);
      } else {
        // Message complete
        const updatedMessages = [...this.messages()];
        updatedMessages[messageIndex] = {
          ...updatedMessages[messageIndex],
          isTyping: false,
          isComplete: true
        };
        this.messages.set(updatedMessages);

        // If this is the first message, add and start the second one after a delay
        if (messageIndex === 0 && this.secondMessage) {
          setTimeout(() => {
            // Add the second message to the array
            const updatedMessages = [...this.messages()];
            updatedMessages.push({
              text: this.secondMessage,
              displayedText: '',
              isTyping: false,
              isComplete: false,
              isUser: false
            });
            this.messages.set(updatedMessages);

            // Start typing the second message
            this.startTypewriter(1);
          }, 1000); // 1 second delay between messages
        }

        // If this is the second message, show the input field
        if (messageIndex === 1) {
          this.showMessageInput.set(true);
        }
      }
    };

    // Start typing after a short delay
    setTimeout(() => {
      typeNextChar();
    }, messageIndex === 0 ? 500 : 0);
  }

  sendUserMessage(): void {
    if (!this.userInput || this.userInput.trim().length === 0) {
      return;
    }

    // Add user message
    const userMessage = this.userInput.trim();
    const updatedMessages = [...this.messages()];
    updatedMessages.push({
      text: userMessage,
      displayedText: userMessage,
      isTyping: false,
      isComplete: true,
      isUser: true
    });
    this.messages.set(updatedMessages);

    // TODO: Handle user message (send to AI, process response, etc.)
    console.log('User message:', userMessage);

    // Clear input after sending
    this.userInput = '';
  }

  selectQuickStartOption(option: { id: number, colour: string, name: string, description: string, prompt: string }): void {
    // Mark that user has selected an option
    this.hasUserSelectedOption.set(true);
    this.selectedQuickStartOption.set(option);
    this.showMessageInput.set(false);

    // Add user message with the selected option
    const userMessage = option.name;
    const updatedMessages = [...this.messages()];
    updatedMessages.push({
      text: userMessage,
      displayedText: userMessage,
      isTyping: false,
      isComplete: true,
      isUser: true
    });
    this.messages.set(updatedMessages);

    console.log('Selected option:', option);

    this.simulateThinking();
    this.runSelectSearch();
  }

  sendTypeQuickStartAnswer() {
    console.log('sendTypeQuickStartAnswer() -> ', this.userInput);
    if (!this.userInput || this.userInput.trim().length === 0) {
      return;
    }

    // Mark that user has selected an option
    this.hasUserSelectedOption.set(true);
    this.selectedQuickStartOption.set(
      { id: -1, colour: 'bg-emerald-500', name: 'Custom', description: 'User has set their own answer', prompt: this.userInput.trim() }
    );
    this.showMessageInput.set(false);

    const userMessage = this.userInput.trim();
    const updatedMessages = [...this.messages()];
    updatedMessages.push({
      text: userMessage,
      displayedText: userMessage,
      isTyping: false,
      isComplete: true,
      isUser: true
    });
    console.log('updatedMessages -> ', updatedMessages[updatedMessages.length - 1]);
    this.messages.set(updatedMessages);

    this.simulateThinking();
    this.runSelectSearch();
  }

  simulateThinking() {
    const thinkingMessages = [
      "Thanks for your input! Let me think about this...",
      "Analyzing your product and requirements...",
      "Crafting the perfect brief for you...",
      "Taking everything into consideration, Brand, Product, Goals...",
      "Almost there, just putting the finishing touches..."
    ];

    let messageIndex = 0;
    const addThinkingMessage = () => {
      if (messageIndex < thinkingMessages.length && this.queryRunning()) {
        const thinkingMessage = thinkingMessages[messageIndex];
        const updatedMessages = [...this.messages()];
        const newMessageIndex = updatedMessages.length;
        
        updatedMessages.push({
          text: thinkingMessage,
          displayedText: '',
          isTyping: false,
          isComplete: false,
          isUser: false
        });
        this.messages.set(updatedMessages);
        
        // Start typing the thinking message
        setTimeout(() => {
          this.startTypewriter(newMessageIndex);
        }, 100);
        
        messageIndex++;
        
        // Calculate delay: typing time + pause before next message
        const typingTime = thinkingMessage.length * 30;
        const pauseTime = 1500;
        
        // Add next thinking message after current one finishes typing
        if (messageIndex < thinkingMessages.length) {
          setTimeout(addThinkingMessage, typingTime + pauseTime);
        }
      }
    };

    // Start adding thinking messages after a short delay
    setTimeout(() => {
      addThinkingMessage();
    }, 500);
  }

  runSelectSearch() {
    console.log('Running search...');
    if (this.selectedQuickStartOption() && this.createBriefService.selectedProduct() && !this.queryRunning()) {
      this.queryRunning.set(true);
      this.startPrompt.set(`This is my brand name: ${this.currentUserService.focussedBrand.companyName}
      This it my brand description: ${this.currentUserService.focussedBrand.about}
      ${this.createBriefService.basicInfoModel()?.briefTitle ? 'This is the current brief title: ' + this.createBriefService.basicInfoModel()?.briefTitle : ''}
      This is the product name they are advertising: ${this.createBriefService.selectedProduct()!.productName}
      This is the product description: ${this.createBriefService.selectedProduct()!.productDescription}
      This is the goal of the campaign: ${this.createBriefService.selectedBriefGoal()?.name ?? ''}: ${this.createBriefService.selectedBriefGoal()?.description ?? ''}


      This information is already displayed to the creators so do not repeat it in the briefText.

      Ignore all previous formats!

      Here is the format of example JSON output:
      {
        "briefText": string,
        "dos": string,
        "donts": string,
        "creativeConcepts": { id: number, name: string, description: string }[]
      }

      briefText is a string and is the suggested brief as outlined below
      dos is a string and should be all the things that a creator should do in the video.
      donts is a string and should be all the things that a creator should avoid in the video. When writing the list of "donts", please include the following items:
        ${!this.createBriefService.selectedUgcOnly() ? `- 🚫 Don't post until your content has been approved.` : ''}
        - 🚫 No explicit language or sounds, nudity, lewd, or malicious posts.
        - 🚫 Do NOT mention or show any competing brands
        - 🚫 No third-party content or trademarks to be used in your content due to rights
        Do not copy and past the above items verbatim, user them as inspiration and write what you think makes sense within this context. 

      1. Write a brief explaining clearly
        a) what the product is or does with a lot of adjectives
        b) how the content creators should make their video

      2. Include these headings. Use different professional wording for the heading text.
      "Overview" -  an overview of what the creator needs to do for the brief
      "What we're looking for in your content" - Give a brief explanation of the sort of content we're looking for
      "Some client USP's for you to include in your content" - List client USP's that you want the creator to include
      This brief should be saved to the "briefText" field in the output JSON object.

      3. Please include 2 to 4 Creative Concepts. There should never be more than 4 Creative Concepts. These creative concepts should be distinct concept or direction for their video to take. Please be creative with your concept suggestions.
      3.1. The creative concepts should be added to the output JSON object undert the "creativeConcepts" field as an array. The id field should start from 0 and increment for each concept you've created.

      3. Add line breaks, bullet points where appropriate
      4. Please return as an HTML string with your formatting done using standard HTML tags.
      5. Make sure to add things that a creator must do in the video also returned as valid HTML formatted with standard HTML tags.
      6. Make sure to add things that a creator must avoid in the video also returned as valid HTML formatted with standard HTML tags.

      Important:
      1. When building the HTML, please use Tailwind CSS classes.
      1.1. For heading (<h2>) elements apply the style: "text-md line-6 font-medium text-gray-900 dark:text-gray-100 mb-1"
      1.2. For paragraph (<p>) elements apply the style: "text-sm text-gray-700 dark:text-gray-300 mb-6"
      `);

      this.userInput = this.selectedQuickStartOption()?.prompt ?? '';
      this.sendMessage();
    } else {
      console.warn('Nissing something...')
    }
  }

  async sendMessage() {
    console.log('Sending message...');
    const messages: GptCompletionMessage[] = [{
      role: "system",
      content: `You are a machine that only returns and replies with valid, iterable RFC8259 compliant JSON in your responses.
        You are a social media expert giving advice and helping a brand create a brief to instruct content creators.
        You decide whether or not a client is asking you to create a new brief/idea`
    },
    {
      role: 'user',
      content: `Is the user input '${this.userInput}' asking you to:

        1. Create a brief or idea?
        2. Or are they chatting to you?

        Here is the format of example JSON output:
        {
          "answer": number
        }`
    }];

    this.chatGptService.sendCompletionMessage(messages).subscribe((res: any) => {
      console.log('Completion Message Sent:', res);
      const content = JSON.parse(res.choices[0].message.content);
      this.startConversation();
    });
  }

  async startConversation() {
    console.log('Starting conversation...');
    if (this.userInput) {
      // add user message to displayed message
      // update prompt messages to be sent to gpt
      this.openAiMessages.push({
        role: "system",
        content: `You are a machine that only returns and replies with valid, iterable RFC8259 compliant JSON in your responses. Your text and punctuation should be compliant for white-space: pre-line.
         You are a social media expert helping a brand come up with a brief to instruct content creators.`
      })
      this.openAiMessages.push({
        role: 'user',
        content: `${this.startPrompt}
        User input: ${this.userInput}`
      });

      this.chatGptService.sendCompletionMessage(this.openAiMessages).subscribe((res: any) => {
        // this.briefText = this.magicBriefService.currentBriefTexts[0];
        let aiBriefObject = JSON.parse(res.choices[0].message.content);
        console.log('AI brief object:', aiBriefObject);

        this.createBriefService.stepsHtml.set(aiBriefObject.briefText);
        this.createBriefService.doHtml.set(aiBriefObject.dos);
        this.createBriefService.dontHtml.set(aiBriefObject.donts);
        
        // Add completion message
        this.addCompletionMessage();
      });
    } else {
      console.log('No user input...')
    }
  }

  addCompletionMessage() {
    const completionMessage = "Perfect! I've created a comprehensive brief for you. Let me show you what I've prepared...";
    const updatedMessages = [...this.messages()];
    updatedMessages.push({
      text: completionMessage,
      displayedText: '',
      isTyping: true,
      isComplete: false,
      isUser: false
    });
    this.messages.set(updatedMessages);
    
    // Start typing the completion message
    const currentMessageIndex = updatedMessages.length - 1;
    this.startTypewriter(currentMessageIndex);

    this.reviewContentMode.set(true);
    
    // Mark query as complete
    this.queryRunning.set(false);
  }

  onHtmlContentChange(event: string, key: string) {
    console.log('onHtmlContentChange() -> ', event);
    console.log('onHtmlContentChange() -> ', key);
    switch (key) {
      case 'key':
        this.createBriefService.stepsHtml.set(event);
        this.createBriefService.description.set(event);
        break;
      case 'do':
        this.createBriefService.doHtml.set(event);
        break;
      case 'dont':
        this.createBriefService.dontHtml.set(event);
        break;
    }
  }
}
